import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";

import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { QFilterMgr } from "@coreapp/CoreApp/QFilterMgr";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { useChartRuleController } from "../Utils/useChartRuleController";

type StateBlock = {
  filterBlock: Nullish<QFilterBlock>;
  index: Nullish<number>;
};

export const useFilterController = () => {
  const filterMgr: Nullish<QFilterMgr> = GetDoc()?.GetFilterMgr();
  const contextId = filterMgr?.GetContextId();
  const sourceFilterBlocksNumber = filterMgr?.GetFilterBlocks(contextId).length;

  const [filterBlocks, setFilterBlocks] = useState<QFilterBlock[]>([]);
  const [filterBlockState, setFilterBlockState] = useState<StateBlock>({
    filterBlock: null,
    index: null
  });
  const skipResetRef = useRef(false);

  // Export PDF might set the block to empty without changing the contextId
  useEffect(() => {
    const wsMgr = GetWSMgr();
    if (!filterMgr || !wsMgr || !contextId) {
      return;
    }

    setFilterBlocks([...filterMgr.GetFilterBlocks(contextId)]);
    if (skipResetRef.current) {
      skipResetRef.current = false;
    } else {
      setFilterBlockState({ filterBlock: null, index: null });
    }
    return wsMgr.SubscribeToTopic(`FilterUpdate_${contextId}`, setFilterBlocks);
  }, [sourceFilterBlocksNumber, contextId]);

  const { getUniqueBlockName, handleNameChangeValidation } = useChartRuleController({
    entity: "Filter",
    elements: filterBlocks
  });

  const setEditingBlock = (block: Nullish<QFilterBlock>, blockIndex: Nullish<number>) => {
    if (block != null) {
      skipResetRef.current = true;
    }
    setFilterBlockState(() => {
      return {
        filterBlock: block,
        index: blockIndex
      };
    });
  };

  const handleBlockNameChange = (blockIndex: number, newName: string) => {
    const nextFilterBlocks = [...filterBlocks];
    const block = nextFilterBlocks[blockIndex];
    block.SetName(newName);
    handleFilterBlocksChange(nextFilterBlocks);
  };

  const handleBlockToggle = (blockIndex: number) => {
    const nextFilterBlocks = [...filterBlocks];
    const block = nextFilterBlocks[blockIndex];
    block.SetEnabled(!block.GetEnabled());
    handleFilterBlocksChange(nextFilterBlocks);
  };

  const handleStartEditing = (block: QFilterBlock, blockIndex?: number) => {
    setEditingBlock(block, blockIndex ?? filterBlockState.index);
  };

  const handleStopEditing = () => {
    setEditingBlock(null, null);
  };

  const handleApplyFilters = () => {
    if (!filterMgr) {
      return;
    }

    filterMgr.StoreFilters();
    if (isInReporting()) {
      return;
    }

    const wsMgr = GetWSMgr();
    wsMgr?.ApplyChartFilters();
    const doc = wsMgr?.GetDoc();
    if (doc) {
      const scm = wsMgr?.GetSubChartMgr();
      const curSubChart = scm?.GetCurrentSubChart();
      const topBoxObj = curSubChart?.GetTopDBObj();
      const topBoxId = topBoxObj?.GetObjectRecordID();

      if (!doc.IsCustomChart()) {
        wsMgr?.DoProgress(appstr("Applying filter"));
        scm?.PurgeLoadCache();
        doc?.DoChartDataQuery(topBoxId);
      }
    }
    ReRenderCanvas();
  };

  const debounceTimer = isInReporting() ? 0 : 500;
  const debouncedApplyFilter = debounce(handleApplyFilters, debounceTimer);

  const handleFilterBlocksChange = (nextFilterBlocks: QFilterBlock[]) => {
    if (!filterMgr || !contextId) {
      return;
    }

    filterMgr.SetFilterBlocks(contextId, nextFilterBlocks);
    debouncedApplyFilter();
  };

  const handleCommit = (block: QFilterBlock) => {
    const nextFilterBlocks = [...filterBlocks];
    const blockIndex = filterBlockState.index;

    if (block != null) {
      if (blockIndex === null || blockIndex === undefined) {
        nextFilterBlocks.push(block);
      } else if (block != null) {
        nextFilterBlocks[blockIndex] = block;
      }
    } else {
      console.log("Trying to commit a null filter block. This should not happen.");
    }

    handleFilterBlocksChange(nextFilterBlocks);
  };

  const handleAddBlock = (rule?: QRulePair) => {
    if (!filterMgr) {
      return;
    }

    const filterBlock = new QFilterBlock();

    filterBlock.InitFilterBlock("");
    filterBlock.SetContextId(filterMgr.GetContextId());

    let filterRule = rule;
    if (!filterRule) {
      filterRule = new QRulePair();
      filterRule.InitRulePair("", "", null, "equals", "");
    }

    filterBlock.AddRule(filterRule);

    setEditingBlock(filterBlock, null);
  };

  const handleBlockRemove = (blockIndex: number) => () => {
    const nextFilterBlocks = [...filterBlocks];
    nextFilterBlocks.splice(blockIndex, 1);
    handleFilterBlocksChange(nextFilterBlocks);
  };

  const getCopyName = (originalName: string): string => {
    const existingNames = new Set(filterBlocks.map(b => b.GetName() ?? ""));
    const suffix = appstr("copy");
    let candidate = `${originalName} ${suffix}`;
    if (!existingNames.has(candidate)) return candidate;
    let i = 2;
    while (existingNames.has(`${originalName} ${suffix} ${i}`)) i++;
    return `${originalName} ${suffix} ${i}`;
  };

  const buildCopiedBlock = (definition: any): QFilterBlock | null => {
    if (!filterMgr || !contextId) return null;
    const block = new QFilterBlock();
    block.LoadFromJSON({ ...definition, contextId, enabled: false });
    block.SetName(getCopyName(definition.name || ""));
    return block;
  };

  const handleCopyBlock = (definition: any) => {
    const block = buildCopiedBlock(definition);
    if (!block) return;
    handleFilterBlocksChange([...filterBlocks, block]);
  };

  const handleCopyAndEditBlock = (definition: any) => {
    const block = buildCopiedBlock(definition);
    if (!block) return;
    const newIndex = filterBlocks.length;
    handleFilterBlocksChange([...filterBlocks, block]);
    handleStartEditing(block, newIndex);
  };

  return {
    debouncedApplyFilter,
    filterBlocks,
    getUniqueBlockName,
    handleStartEditing,
    handleStopEditing,
    handleBlockRemove,
    handleAddBlock,
    handleCommit,
    handleCopyBlock,
    handleCopyAndEditBlock,
    handleBlockNameChange,
    handleBlockToggle,
    handleNameChangeValidation,
    filterBlockState
  };
};
